import { announce } from "../lib/announce.ts";
import { cartChange, fetchCartSection } from "../lib/cart-api.ts";
import { Component } from "../lib/component.ts";
import { parseLineItemProperties } from "../lib/line-item-properties.ts";

interface ModalPanelLike extends HTMLElement {
  open: () => void;
  close: () => void;
  title: string;
}

function isModalPanelLike(el: Element | null): el is ModalPanelLike {
  if (!(el instanceof HTMLElement)) return false;
  const open: unknown = Reflect.get(el, "open");
  const close: unknown = Reflect.get(el, "close");
  return typeof open === "function" && typeof close === "function";
}

interface LineItemContext {
  key: string;
  quantity: number;
  properties: Record<string, string>;
}

class CartNote extends Component {
  #modal: ModalPanelLike | null = null;

  #context: LineItemContext = { key: "", quantity: 0, properties: {} };

  get #noteKey(): string {
    return this.dataset.noteKey ?? "Personal note";
  }

  /** Line-item property keys for the gift tag's To / From lines. */
  get #toKey(): string {
    return this.dataset.toKey ?? "To";
  }

  get #fromKey(): string {
    return this.dataset.fromKey ?? "From";
  }

  /**
   * Set whenever a tag is added, even with every field blank: customers may
   * want an empty tag to write by hand, and fulfillment needs a signal that a
   * tag belongs in the box. Cleared on remove.
   */
  get #tagKey(): string {
    return this.dataset.tagKey ?? "Gift tag";
  }

  /**
   * Mirrors the cart section's "Offer Gift Tag" setting. Off (the default
   * while tags are out of stock), the To / From inputs are not rendered, no
   * tag marker is written, and a note needs text to be saved.
   */
  get #giftTagEnabled(): boolean {
    return this.dataset.giftTag === "true";
  }

  #hasTag(props: Record<string, string>): boolean {
    return Boolean(
      props[this.#tagKey] || props[this.#noteKey] || props[this.#toKey] || props[this.#fromKey],
    );
  }

  #tagInput(name: "to" | "from"): HTMLInputElement | null {
    const input = this.#modal?.querySelector(`input[name="${name}"]`);
    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * Write an optional value into the property set: a filled value is saved, an
   * emptied one is sent as "" so Shopify clears it, and a key the line never
   * had is left out so unrelated payloads stay untouched.
   */
  #assignOptional(properties: Record<string, string>, key: string, value: string): void {
    if (value) {
      properties[key] = value;
    } else if (key in properties) {
      properties[key] = "";
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    const modalEl = this.querySelector("modal-panel");
    this.#modal = isModalPanelLike(modalEl) ? modalEl : null;
    document.addEventListener("click", this.#handleClick, {
      signal: this.signal,
    });
    this.addEventListener("submit", this.#handleSubmit, {
      signal: this.signal,
    });
    this.addEventListener("input", this.#handleInput, {
      signal: this.signal,
    });
  }

  #handleInput = (): void => {
    const form = this.#modal?.querySelector("form");
    const textarea = form?.querySelector("textarea");
    if (!(textarea instanceof HTMLTextAreaElement)) return;

    const { selectionStart, selectionEnd } = textarea;
    const normalized = textarea.value.replace(/\n{3,}/g, "\n\n");

    if (textarea.value !== normalized) {
      textarea.value = normalized;
      textarea.setSelectionRange(
        Math.min(selectionStart, normalized.length),
        Math.min(selectionEnd, normalized.length),
      );
    }
  };

  #handleClick = (event: MouseEvent): void => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-action="edit-note"], [data-action="remove-note"]');
    if (!(button instanceof HTMLButtonElement)) return;

    const context = this.#parseLineItemContext(button);
    if (!context) return;

    if (button.dataset.action === "edit-note") {
      this.#openEditor(context);
    } else {
      this.#removeNote(button, context);
    }
  };

  #openEditor(context: LineItemContext): void {
    this.#context = context;
    this.#modal?.open();

    const noteKey = this.#noteKey;
    const props = this.#context.properties;
    const hasNote = this.#hasTag(props);

    const toInput = this.#tagInput("to");
    if (toInput) toInput.value = props[this.#toKey] ?? "";
    const fromInput = this.#tagInput("from");
    if (fromInput) fromInput.value = props[this.#fromKey] ?? "";

    const textarea = this.#modal?.querySelector("textarea");
    if (textarea instanceof HTMLTextAreaElement) {
      textarea.value = props[noteKey] ?? "";
      textarea.dispatchEvent(new Event("input", { bubbles: true }));
    }

    const submitButton = this.#modal?.querySelector('[type="submit"]');
    if (submitButton instanceof HTMLButtonElement) {
      submitButton.textContent = hasNote
        ? (this.dataset.editText ?? "")
        : (this.dataset.addText ?? "");
    }
  }

  #removeNote(button: HTMLButtonElement, context: LineItemContext): void {
    const { key, quantity, properties } = context;
    properties[this.#noteKey] = "";
    this.#assignOptional(properties, this.#toKey, "");
    this.#assignOptional(properties, this.#fromKey, "");
    this.#assignOptional(properties, this.#tagKey, "");

    const originalText = button.textContent ?? "";
    button.disabled = true;
    if (button.dataset.loadingText) {
      button.textContent = button.dataset.loadingText;
    }

    this.#updateCart({ key, quantity, properties }, this.dataset.announcementRemoved).catch(() => {
      button.disabled = false;
      button.textContent = originalText;
      if (this.dataset.announcementError) announce(this.dataset.announcementError);
    });
  }

  #handleSubmit = (event: SubmitEvent): void => {
    event.preventDefault();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const textarea = form.querySelector("textarea");
    const submitButton = form.querySelector('[type="submit"]');
    if (!(textarea instanceof HTMLTextAreaElement) || !this.#context.key) return;

    const note = textarea.value.trim();
    const to = this.#tagInput("to")?.value.trim() ?? "";
    const from = this.#tagInput("from")?.value.trim() ?? "";

    const properties = { ...this.#context.properties };

    if (this.#giftTagEnabled) {
      // No validation: a fully blank submission is a request for a blank tag to
      // write by hand. The marker says a tag belongs in the box; each text field
      // is saved when filled, or sent as "" to clear a value the line had before.
      properties[this.#tagKey] = "Yes";
      this.#assignOptional(properties, this.#noteKey, note);
      this.#assignOptional(properties, this.#toKey, to);
      this.#assignOptional(properties, this.#fromKey, from);
    } else {
      // Without a tag there is nothing to put in the box for a blank note, so
      // an empty submission just returns focus to the field. Any tag values a
      // line picked up while tags were offered are cleared on the next save.
      if (!note) {
        textarea.focus();
        return;
      }
      properties[this.#noteKey] = note;
      this.#assignOptional(properties, this.#toKey, "");
      this.#assignOptional(properties, this.#fromKey, "");
      this.#assignOptional(properties, this.#tagKey, "");
    }

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
      submitButton.dataset.originalText = submitButton.textContent ?? "";
      if (this.dataset.savingText) {
        submitButton.textContent = this.dataset.savingText;
      }
    }

    const { key, quantity } = this.#context;

    this.#updateCart({ key, quantity, properties }, this.dataset.announcementSaved)
      .then(() => {
        this.#modal?.close();
      })
      .catch(() => {
        if (this.dataset.announcementError) announce(this.dataset.announcementError);
      })
      .finally(() => {
        if (submitButton instanceof HTMLButtonElement) {
          submitButton.disabled = false;
          if (submitButton.dataset.originalText) {
            submitButton.textContent = submitButton.dataset.originalText;
          }
          delete submitButton.dataset.originalText;
        }
      });
  };

  #parseLineItemContext(button: HTMLButtonElement): LineItemContext | null {
    const key = button.dataset.lineItemKey ?? "";
    const quantity = Number(button.dataset.quantity);
    if (!key || !(quantity > 0)) return null;

    let rawProps: unknown;
    try {
      rawProps = JSON.parse(
        button.closest("[data-properties]")?.getAttribute("data-properties") ?? "[]",
      );
    } catch {
      return null;
    }

    const properties = parseLineItemProperties(rawProps);
    if (!properties) return null;

    return { key, quantity, properties };
  }

  #updateCart(
    { key, quantity, properties }: LineItemContext,
    announcement: string | undefined,
  ): Promise<void> {
    return cartChange({ id: key, quantity, properties })
      .then(() => fetchCartSection("cart-drawer"))
      .then((html) => {
        document.dispatchEvent(new CustomEvent("cart:render", { detail: { html, announcement } }));
      });
  }
}

if (!customElements.get("cart-note")) {
  customElements.define("cart-note", CartNote);
}
